import {
  Deal,
  DiscountConfiguration,
  getConvertedPrice,
  PackageConfig,
  PriceValue,
} from '@ca/common/types'
import getPackageLabels from '@order/getPackageLabels'
import { useActiveDeal } from '@order/hooks/useDeal'
import useDealSettings, {
  useCustomDealSettings,
} from '@order/hooks/useDealSettings'
import { usePackageVariant } from '@order/hooks/usePackages'
import useStartOrderConfig from '@order/hooks/useStartOrderConfig'
import { Discounts } from '@order/utils/discounts/Discounts'
import OptionDefault from '@order/steps/templates/Default/Option/OptionDefault'
import { isValueOptionColor, OptionColor } from '@order/steps/types'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  item: PackageConfig
  basePackage?: PackageConfig
  deal?: Deal
  count: number
  discounts?: DiscountConfiguration[]
}

const packageIcons: Partial<Record<OptionColor, string>> = {
  default: require('@/images/packages/standard.png'),
  gray: require('@/images/packages/standard.png'),
  green: require('@/images/packages/premium.png'),
  magenta: require('@/images/packages/high-end.png'),
  blue: require('@/images/packages/steiner.png'),
}

const getDiscountedPrice = (
  price: number,
  variable: string,
  discounts?: DiscountConfiguration[]
) => {
  const discount = discounts?.filter(
    (d) => d.scope === 'variable' && d.variable === variable
  )

  const wrapped = Discounts.wrap(price)
  return Discounts.applyDiscounts(wrapped, discount)
}

export default function PackagesItem({
  item,
  deal,
  count,
  basePackage,
  discounts,
}: Props) {
  const { t, i18n } = useTranslation(['packages'])
  const variant = usePackageVariant(item)
  const baseVariant = usePackageVariant(basePackage)
  const activeDeal = useActiveDeal()
  const settings = useCustomDealSettings(deal ?? activeDeal)

  const config = useRemoteConfig()
  const region = config?.region

  const { toStep } = useStartOrderConfig()

  const color = variant?.color || item.color

  const colorOption = isValueOptionColor(color) ? color : 'default'
  // const key = variant?.value || item.value

  const image = variant?.image || item.image
  const price = useMemo(() => {
    const basePriceSrc = baseVariant ? baseVariant.price : basePackage?.price
    const priceSrc = variant ? variant.price : item.price
    const basePrice = PriceValue.safe(basePriceSrc, region)
    const fullPrice = Math.max(PriceValue.safe(priceSrc, region) - basePrice, 0)
    const convertedPrice = getConvertedPrice(fullPrice, settings)
    return getDiscountedPrice(convertedPrice, 'upgrade', discounts)
  }, [region, discounts, basePackage, baseVariant, item, variant, settings])

  const { title, desc } = useMemo(
    () =>
      getPackageLabels({
        value: variant?.value || item.value,
        name: item.name,
        i18n,
      }),
    [item, variant, i18n]
  )

  const imageFallback = color ? packageIcons[colorOption] : undefined

  return (
    <OptionDefault
      id={item.id}
      onPress={() => toStep(item, deal)}
      title={t(title)}
      desc={t(desc)}
      options={count}
      image={image?.url ?? imageFallback}
      price={price}
      colorOption={colorOption}
      priceSuffix={settings.priceSuffix}
    />
  )
}
